import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import to from 'await-to-js';
import { concatMap, of } from 'rxjs';
import * as moment from 'moment';

import { BooksService } from '../../../core/services/data/books.service';
import { UtilitiesService } from '../../../core/services/utilities.service';
import { ModalService } from '../../../core/services/modal.service';
import { PromoCodeService } from '../../../core/services/data/promo-code.service';
import { AnalyticsService } from '../../../core/services/analytics.service';
import { UserService } from '../../../core/services/data/user.service';
import { OrderService } from '../../../core/services/data/order.service';
import { UpsellService } from '../../../core/services/data/upsell.service';
import { PurchaseProcessService } from '../../../core/services/data/purchase-process.service';
import { ExperimentsService } from '../../../core/services/experiments.service';
import { FeaturedBooksService } from '../../../core/services/data/featured-books.service';

import { IUser } from '../../../types/datatypes';

// constants
const AUDIO_THICKET_SLUG = 'audio-thicket';
const DEFAULT_BOOK_COVER = '/assets/img/defaults/default_book_image_400w_simple.jpg';
const DEFAULT_AUDIO_BOOK_COVER = '/assets/img/defaults/default_book_image_square_simple.jpg';
const AUTHOR_DASHBOARD = '/author-dashboard';
const ORDER_CHECKOUT = 'order-checkout';
const FEATURES = 'features';
const PROMO_TYPE_PERCENT = 'percent';
const PROMO_TYPE_AMOUNT = 'amount';
const PROMO_TYPES = [PROMO_TYPE_PERCENT, PROMO_TYPE_AMOUNT];

@Component({
	selector: 'app-features-step5',
	templateUrl: './features-step5.component.html',
	styleUrls: ['./features-step5.component.scss'],
})
export class FeaturesStep5Component implements OnInit, OnDestroy {
	@Input() currentState;

	@Input() info;

	@Input() purchaseSource;

	@Output() stepChange = new EventEmitter();

	@Output() soldOutEvent = new EventEmitter();

	public moment = moment;

	public errorText;

	public errorTitle;

	public form: UntypedFormGroup;

	public freePayment = false;

	public promoCodeCommon;

	public applicationResult: { discountAmount?: number; paymentAmount?: number; type: 'percent' | 'amount'; value: number };

	public paymentAmount;

	public discountAmount;

	public promoCode;

	public promoError;

	public paymentInProgress = false;

	public upsellApplyingDiscount = false;

	public upsellPaymentAmount;

	public upsellDiscountAmount;

	public klaviyoSent = false;

	public success = false;

	public addingToCart = false;

	public paypal;

	public errorButtons;

	public defaultCoverImage = DEFAULT_BOOK_COVER;

	public isUpsellVisible = false;

	public discountText: string;

	public upsell;

	public pubDate: string;

	public errorOptions = {
		title: '',
		text: null,
		actions: [],
	};

	private order$;

	private order;

	private checking = false;

	private user;

	constructor(
		private bs: BooksService,
		private modalService: ModalService,
		private router: Router,
		private pcs: PromoCodeService,
		private us: UtilitiesService,
		private as: AnalyticsService,
		private uss: UserService,
		private os: OrderService,
		private ups: UpsellService,
		private purchaseProcessService: PurchaseProcessService,
		private route: ActivatedRoute,
		private es: ExperimentsService,
		private fbs: FeaturedBooksService,
	) {
	}

	get variant() {
		return this.es.getVariant();
	}

	get siteSlug() {
		return this.purchaseProcessService.siteSlug;
	}

	get siteAdvert() {
		return this.purchaseProcessService.siteAdvert;
	}

	get siteAdvertSlug() {
		return this.purchaseProcessService.siteAdvertSlug;
	}

	get featureId() {
		return this.purchaseProcessService.featureId;
	}

	get featuredBook() {
		return this.purchaseProcessService.feature;
	}

	get authorLink() {
		return this.purchaseProcessService.authorLink;
	}

	get loading$() {
		return this.purchaseProcessService.loading$;
	}

	featureHasValidSiteAdvert(feature): boolean {
		return !!feature?._siteAdvert?.price;
	}

	featureHasValidPromoCode(feature): boolean {
		return PROMO_TYPES.includes(feature?._promoCode?.discountType);
	}

	ngOnInit() {
		const sub = this.loading$.subscribe(async (loading) => {
			if (!loading) {
				await this.initialize();
				sub.unsubscribe();

				// If the page is refreshed the observer will emit a loading value of false.
				// If the data is already available through the normal process, then it is already loaded prior to page init.
				// WHY cant we initialize here? Hopefully next refactor will make this clear.
				// I have no idea if this will work with other promo codes.
				// When this is less of a black box.... we can make this more robust.
				// 2023-09-11 - Jordan.
				if (!this.purchaseProcessService.loading) {
					this.paymentAmount = this.featuredBook.paymentAmount;
					this.discountAmount = this.featuredBook.discountAmount;
					this.applicationResult = {
						type: this.featuredBook._promoCode?.discountType === 'percent' ? PROMO_TYPE_PERCENT : PROMO_TYPE_AMOUNT,
						value: this.featuredBook._promoCode?.value || 0,
					};
					this.promoCodeDiscountText(this.featuredBook._promoCode?.code);
					this.checkForMembership();
				}
			}
		});
		// Already loaded when init is called.
		if (!this.purchaseProcessService.loading) {
			this.paymentAmount = this.featuredBook.paymentAmount;
			this.discountAmount = this.featuredBook.discountAmount;
			this.applicationResult = {
				type: this.featuredBook._promoCode?.discountType === 'percent' ? PROMO_TYPE_PERCENT : PROMO_TYPE_AMOUNT,
				value: this.featuredBook._promoCode?.value || 0,
			};
			this.promoCodeDiscountText(this.featuredBook._promoCode?.code);
			this.checkForMembership();
		}
	}

	async initialize() {
		if (this.siteSlug === AUDIO_THICKET_SLUG) {
			this.defaultCoverImage = DEFAULT_AUDIO_BOOK_COVER;
		}
		this.order$ = this.os.orderStore$.subscribe((data: any) => {
			this.order = data;
		});

		if (!this.featuredBook) return;
		if (this.featuredBook.paymentStatus === 'paid') {
			const pathParts = window.location.pathname.split('/');
			const path = `/features/${pathParts[2]}/${this.featuredBook._id}/success`;
			this.stepChange.emit({ step: 6, path });
		}
		this.paymentAmount = this.featuredBook.paymentAmount;
		// this.klaviyoTrack();
	}

	ngOnDestroy() {
		this.order$?.unsubscribe();
	}

	promoCodeDiscountText(code: string) {
		if (!code) {
			this.discountText = null;
			return;
		}
		this.discountText = code.slice(0, 4) === 'MEM-' ? 'Member Discount' : 'Discount';
	}

	checkForMembership() {
		// Shouldn't run if there's already a promoCode.
		// This means that the customer is trying to apply a promoCode, or one is already applied.
		if (this.featuredBook._promoCode) {
			return;
		}
		this.uss.author$.pipe(concatMap((author: IUser) => {
			if (!author) {
				return of(null);
			}
			return this.pcs.getMembershipPromocode(author.username);
		})).subscribe((result: any) => {
			if (result && result.success && result.promoCode) {
				const discountAmount = (this.featuredBook.paymentAmount + this.featuredBook.discountAmount) * 0.1;
				const remainingValue = result.promoCode.discountLimit - result.promoCode.currentLimit;

				if (remainingValue < discountAmount) {
					this.discountAmount = remainingValue;
					this.paymentAmount = (this.featuredBook.paymentAmount + this.featuredBook.discountAmount) - remainingValue;
				} else {
					this.discountAmount = discountAmount;
					this.paymentAmount = (this.featuredBook.paymentAmount + this.featuredBook.discountAmount) - discountAmount;
				}

				this.applicationResult = {
					type: PROMO_TYPE_PERCENT,
					value: 10,
				};
				this.promoCodeDiscountText(result.promoCode.code);
			}
		});
	}

	applyPromoCode(code: string) {
		if (!code) {
			this.promoError = 'Please enter a valid promo code.';
			return;
		}

		this.pcs.apply(code, this.featuredBook._id, 'featuredBook')
			.subscribe((result: any) => {
				if (result.success) {
					this.promoError = null;
					this.applicationResult = result.data;
					this.paymentAmount = this.applicationResult.paymentAmount;
					this.discountAmount = this.applicationResult.discountAmount;
					this.promoCodeDiscountText(code);
					this.as.trackAddCoupon(code, this.siteSlug);
				} else {
					this.promoError = result.error || 'Failed to apply promo code.';
				}
			});
	}

	async addToCart(): Promise<void> {
		if (this.addingToCart) return;

		this.addingToCart = true;
		this.featuredBook.purchaseSource = this.purchaseSource;

		this.os.addItem(this.featuredBook._id, 'featuredBook', this.featuredBook)
			.subscribe({
				next: (result: any) => this.handleAddToCartSuccess(),
				error: (err) => this.handleAddToCartFailure(err),
			});
	}

	handleAddToCartSuccess() {
		const siteAdvertId = this.featuredBook._siteAdvert._id;
		this.ups.fetchPossibleUpsell(siteAdvertId, this.featuredBook.pubDate).subscribe((result: any) => {
			if (result.success && result.upsell) {
				this.upsell = result.upsell;
				this.upsellApplyingDiscount = false;
				this.upsellDiscountAmount = 0;
				this.upsellPaymentAmount = this.upsell.to.item.price;
				this.openUpsellModal();
				this.addingToCart = false;
				this.os.setCartVisible();
				return;
			}
			this.addingToCart = false;
			this.os.setCartVisible();
			this.goHome();
		});
	}

	handleAddToCartFailure(error) {
		this.addingToCart = false;
		const defaultErrorMsg = 'Failed to add feature to cart. Please try again later.';
		const defaultErrorTitle = 'Error!';
		const errorMsg = error && error.message ? error.message : defaultErrorMsg;

		this.errorOptions.title = error && error.duplicate ? 'Whoops!' : defaultErrorTitle;
		this.errorOptions.text = [errorMsg];
		this.errorOptions.actions = error.duplicate ? [
			{ text: 'Check out now', type: 'solid', callback: () => this.checkout() },
			{ text: 'Select another date', type: 'text', callback: () => this.newPromo() },
		] : [{ type: 'solid', text: 'Got it', callback: () => this.modalService.close('fs5-error-modal') }];
		this.modalService.open('fs5-error-modal');
	}

	checkout() {
		this.modalService.close('fs5-error-modal');
		let link = `/${FEATURES}/${ORDER_CHECKOUT}/${this.order._id}`;
		if (this.authorLink) {
			link = `${AUTHOR_DASHBOARD}/${this.authorLink}/${FEATURES}/${ORDER_CHECKOUT}/${this.order._id}`;
		}
		this.os.setCartNotVisible();
		this.router.navigate([link])
			.then((didNavigate) => {
				/*
				 * There's a "bug" with the angular routing service where if it thinks the same URL is trying to be loaded,
				 * it will ignore that request. Below serves as a check for this edge case. If the navigation failed,
				 * 'didNavigate' will be a falsy value and we can then trick the router to navigate it to our original link.
				 */
				if (!didNavigate) {
					this.router.navigate(['/']).then(() => this.router.navigate([link]).then());
				}
			});
	}

	newPromo() {
		this.modalService.close('fs5-error-modal');
		let path = `/${FEATURES}`;
		const pathParts = window.location.pathname.split('/');
		// Build the url path to step 3 since it varies between authenticated & non-authenticated users
		if (pathParts && pathParts.length >= 5) {
			/*
			 * If the user is authenticated, the needed variables live in slots 4 and 5 while
			 * if the user is un-authenticated, the needed variables live in slots 2 and 3.
			 */
			const pathBounds = this.user ? [4, 5] : [2, 3];
			for (let i = pathBounds[0]; i <= pathBounds[1]; i++) {
				path += `/${pathParts[i]}`;
			}
		}
		this.stepChange.emit({ step: 3, path });
	}

	goHome() {
		const link = this.authorLink ? `${AUTHOR_DASHBOARD}/${this.authorLink}/home` : '/home';
		this.router.navigate([link]);
	}

	/**
	 * This is used to apply promo codes by a referrer. According to Ferol this hasn't been used in a long time
	 * but might be used again in the future.
	 */

	// klaviyoTrack() {
	// 	if (!this.featuredBook.referrer) return;

	// 	this.pcs.fetchCodeByReferrer(this.featuredBook.referrer).subscribe(
	// 		(data: any) => {
	// 			if (data.success) {
	// 				if (data.promoCodes[0]) {
	// 					[this.promoCode] = data.promoCodes;
	// 					this.applyPromoCode(this.promoCode.code);
	// 				}
	// 			}
	// 		},
	// 	);
	// }

	openUpsellModal() {
		this.isUpsellVisible = true;
	}

	closeUpsellModal() {
		this.isUpsellVisible = false;
		this.goHome();
	}

	async acceptUpsell() {
		const upsellId = this.upsell._id;
		const featuredBookId = this.featuredBook._id;
		const purchaseSource = 'Upsell';

		const [err, result]: any = await to(this.ups.createUpsellFeature(upsellId, featuredBookId,
			this.upsellApplyingDiscount, this.upsellPaymentAmount, this.upsellDiscountAmount, this.promoCode, purchaseSource));

		if (err) {
			const errorMsg = result.error && result.error.message ? result.error.message : result.error;
			this.errorText = [errorMsg || 'Failed to add upsell to cart.'];
			this.modalService.open('fs5-error-modal');
			return;
		}

		this.closeUpsellModal();
	}

	handleEdit() {
		this.purchaseProcessService.edit(this.featuredBook);
		this.router.navigate(['../'], { relativeTo: this.route });
	}

	resetScroll() {
		window.scrollTo(0, 0);
	}
}
